import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import WBCModal from "../../elements/WBCModal";
import WBCPrimaryOutlinedButton from "../../elements/WBCPrimaryOutlinedButton";
import Select from "react-select";
import Theme from "../../../Theme";
import WBCYearsContext from "../../../contexts/WBCYearsContext";
import ChronicConditionsApi from "../../../httpClients/ChronicConditionsApi";
import ChronicConditionsContext from "../../../contexts/ChronicConditionsContext";
import { useAlert } from "../../hooks/useAlert";

const BodyContent = styled.div``;

BodyContent.displayName = "BodyContent";

const FooterContent = styled.div`
  display: flex;
  flex-direction: row;
  & button {
    margin-left: 0.5rem;
  }
`;

FooterContent.displayName = "FooterContent";

const PlayersToAddContainer = styled.div.attrs(() => ({
  className: "d-flex flex-column"
}))`
  margin: 1.375rem 0;
`;

const PlayerToAdd = styled.div.attrs(() => ({
  className: "d-flex flex-row col-12 align-items-center"
}))`
  background-color: ${Theme.white};
  border: solid 1 ${Theme["grey-three"]};
  border-radius: 0.3rem;
  padding: 0;

  & .remove-player {
    display: flex;
    margin: 0 0.625rem;
    color: ${props => props.theme.wbcBlue};
    cursor: pointer;
    text-align: center;
  }

  & .player-info {
    display: flex;
    padding: 0.625rem 0;
  }
`;

const INITIAL_STATE = {
  allPlayerOptions: [],
  filteredPlayerOptions: [],
  isLoading: false,
  selectedPlayerOption: null,
  selectedPlayers: []
};

const AddChronicConditionPlayerModal = ({ isOpen, setIsOpen, loadChronicConditionsByYearFn }) => {
  const showAlert = useAlert();

  const { selectedYear } = useContext(WBCYearsContext);
  const { playerConditions } = useContext(ChronicConditionsContext);

  const [allPlayerOptions, setAllPlayerOPtions] = useState(INITIAL_STATE.allPlayerOptions);
  const [filteredPlayerOptions, setFilteredPlayerOptions] = useState(INITIAL_STATE.filteredPlayerOptions);
  const [isLoading, setIsLoading] = useState(INITIAL_STATE.isLoading);
  const [selectedPlayerOption, setSelectedPlayerOption] = useState(INITIAL_STATE.selectedPlayerOption);
  const [selectedPlayers, setSelectedPlayers] = useState(INITIAL_STATE.selectedPlayers);

  const playerProfileIdsWithChronicConditions = useMemo(() => playerConditions.map(player => player.profileId), [
    playerConditions
  ]);

  const handleAddToListBtnClick = () => {
    const players = selectedPlayers.length > 1 ? "Players" : "Player";
    ChronicConditionsApi.addChronicConditionForPlayers(
      selectedYear,
      selectedPlayers.map(player => player.ebisId)
    )
      .then(response => {
        loadChronicConditionsByYearFn();
        showAlert(`${players} added`, "success");
      })
      .catch(error => {
        showAlert(`Error adding ${players.toLowerCase()}`, "danger");
      })
      .finally(resetState());
  };

  const handleCancelBtnClick = () => {
    resetState();
  };

  const handleRemovePlayerFromSelectedPlayers = player => {
    setSelectedPlayers(selectedPlayers.filter(p => p !== player));
    setAndSortPlayerOptions([...filteredPlayerOptions, { value: player, label: playerLabel(player) }]);
  };

  const handleSearchPlayers = inputValue => {
    const matchingPlayers = allPlayerOptions.filter(player => {
      return (
        !selectedPlayers.includes(player) &&
        playerDisplayName(player)
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      );
    });
    setAndSortPlayerOptions(playerListToSelectOptions(matchingPlayers));
  };

  const handleSelectPlayerOption = option => {
    setAndSortPlayerOptions(filteredPlayerOptions.filter(player => player.value !== option.value));
    setSelectedPlayers([...selectedPlayers, option.value]);
    setSelectedPlayerOption(null);
  };

  const setAndSortPlayerOptions = playerOptions => {
    setFilteredPlayerOptions(playerOptions.sort((a, b) => a?.label.localeCompare(b.label)));
  };

  const playerDisplayName = player => {
    const rosterFirstName =
      player.rosterFirstName && player.rosterFirstName !== player.firstName ? `(${player.rosterFirstName})` : "";
    return `${player.lastName}, ${player.firstName} ${rosterFirstName}`;
  };

  const playerLabel = useCallback(player => {
    return `${playerDisplayName(player)} - ${player.orgCode && player.orgCode !== "BOC" ? player.orgCode : "N/A"}`;
  }, []);

  const playerListToSelectOptions = useCallback(
    playerList => {
      return playerList.map(player => ({
        value: player,
        label: playerLabel(player)
      }));
    },
    [playerLabel]
  );

  const resetState = () => {
    setAllPlayerOPtions(INITIAL_STATE.allPlayerOptions);
    setFilteredPlayerOptions(INITIAL_STATE.filteredPlayerOptions);
    setIsLoading(INITIAL_STATE.isLoading);
    setSelectedPlayerOption(INITIAL_STATE.selectedPlayerOption);
    setSelectedPlayers(INITIAL_STATE.selectedPlayers);
    setIsOpen(false);
  };

  useEffect(() => {
    const newLoadPlayerList = () => {
      setIsLoading(true);
      ChronicConditionsApi.getChronicConditionPlayerOptions(selectedYear)
        .then(response => {
          setAllPlayerOPtions(response);
        })
        .catch(error => {
          showAlert("Error fetching players", "danger");
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    if (isOpen) {
      newLoadPlayerList();
    }
  }, [isOpen, playerProfileIdsWithChronicConditions, selectedYear, showAlert]);

  useEffect(() => {
    const selectedPlayerOptionEbisIds = selectedPlayers.map(player => player.ebisId);
    const unselectedPlayerOptions = allPlayerOptions.filter(
      player => !selectedPlayerOptionEbisIds.includes(player.ebisId)
    );
    setAndSortPlayerOptions(playerListToSelectOptions(unselectedPlayerOptions));
  }, [allPlayerOptions, playerListToSelectOptions, selectedPlayers]);

  return (
    <WBCModal isOpen={isOpen} headerText="Add Chronic Condition Player" toggleModal={() => handleCancelBtnClick()}>
      <BodyContent>
        <Select
          id="add-player-select"
          placeholder="type to search for a player"
          autoFocus
          value={selectedPlayerOption}
          options={isLoading ? [] : filteredPlayerOptions}
          onChange={handleSelectPlayerOption}
          onInputChange={handleSearchPlayers}
          noOptionsMessage={() => "no matching players found"}
          isLoading={isLoading}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              backgroundColor: state.isFocused ? "#fff7d4" : "#ffffff",
              borderColor: state.isFocused ? "#4c8cee" : baseStyles.borderColor
            }),
            option: baseStyles => ({
              ...baseStyles,
              color: Theme.black,
              backgroundColor: Theme.white,
              ":hover": {
                backgroundColor: "#fff7d4"
              }
            }),
            placeholder: baseStyles => ({
              ...baseStyles,
              color: Theme.grey,
              fontStyle: "italic"
            })
          }}
        />
        <PlayersToAddContainer>
          {selectedPlayers.map(player => (
            <PlayerToAdd key={player.ebisId}>
              <div className="remove-player" onClick={() => handleRemovePlayerFromSelectedPlayers(player)}>
                <i className="fa fa-trash fa-lg" />
              </div>
              <div className="player-info">{playerLabel(player)}</div>
            </PlayerToAdd>
          ))}
        </PlayersToAddContainer>
      </BodyContent>
      <FooterContent>
        <WBCPrimaryOutlinedButton onClick={handleAddToListBtnClick} disabled={!(selectedPlayers?.length > 0)}>
          Add to List
        </WBCPrimaryOutlinedButton>
        <WBCPrimaryOutlinedButton onClick={handleCancelBtnClick}>Cancel</WBCPrimaryOutlinedButton>
      </FooterContent>
    </WBCModal>
  );
};

AddChronicConditionPlayerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  loadChronicConditionsByYearFn: PropTypes.func.isRequired
};

export default AddChronicConditionPlayerModal;
